import { datadogRum } from "@datadog/browser-rum";
import * as Sentry from "@sentry/react";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";

import { userMe } from "../actions/authActions";
import { fetchUserSignatures, fetchFeatureFlags } from "../actions/sysActions";
import {
  useHasLoaded,
  useRequestAfterEverySiteLoad,
} from "../hooks/requestStatusHooks";
import { AuthState, selectIsAccessTokenExpired } from "../reducers/authReducer";

// AuthUserGate should only be rendered if there is an unexpired Access JWT
// It will render a LoadingEl Component until the following conditions are met:
//   1. User has been fetched via /api/users/me/
//   2. User Signatures have been fetched via /api/users/{id}/signatures/
//   3. Feature Flags have been fetched via /api/feature-flags/
//   4. Macro-Code Helpers have been fetched via /api/macro-codes/helpers/
//   5. Maintenance Messages have been fetched via /api/mmaintenance-message/

const AuthUserGate = ({
  children,
  LoadingEl,
}: {
  children: React.ReactNode;
  LoadingEl: ({
    className,
    children,
  }: {
    className?: string;
    children?: React.ReactNode;
  }) => React.ReactElement;
}) => {
  const isAccessTokenExpired = useSelector(selectIsAccessTokenExpired);
  const accessTokenUserId = useSelector(
    ({ auth }: { auth: AuthState }) => auth.access?.user_id
  );
  const userId = useSelector(({ auth }: { auth: AuthState }) => auth.user?.id);
  const userFirstName = useSelector(
    ({ auth }: { auth: AuthState }) => auth.user?.first_name
  );
  const userLastName = useSelector(
    ({ auth }: { auth: AuthState }) => auth.user?.last_name
  );
  const userEmail = useSelector(
    ({ auth }: { auth: AuthState }) => auth.user?.email
  );

  const loadedUser = useHasLoaded(userMe);
  const loadedUserSignatures = useHasLoaded(fetchUserSignatures, {
    userId: accessTokenUserId,
  });
  const loadedFeatureFlags = useHasLoaded(fetchFeatureFlags);

  if (isAccessTokenExpired) {
    throw new Error(
      "AuthUserGate should not be rendered with an expired Access Token"
    );
  }

  if (!accessTokenUserId || (userId && userId !== accessTokenUserId)) {
    throw new Error("The loaded User does not match the Access Token");
  }

  useRequestAfterEverySiteLoad(userMe);
  useRequestAfterEverySiteLoad(fetchUserSignatures, {
    userId: accessTokenUserId,
  });
  useRequestAfterEverySiteLoad(fetchFeatureFlags);

  // Set User on Sentry and Datadog
  useEffect(() => {
    const userArgs = {
      id: `${accessTokenUserId}`,
      name:
        [userFirstName, userLastName].filter((val) => !!val).join(" ") ||
        undefined,
      email: userEmail,
    };

    datadogRum.setUser(userArgs);

    Sentry.configureScope((scope) => scope.setUser(userArgs));
  }, [accessTokenUserId, userFirstName, userLastName, userEmail]);

  // Unset User on Sentry and Datadog when unmounting (session has ended)
  useEffect(() => {
    return () => {
      datadogRum.removeUser();
      Sentry.configureScope((scope) => scope.setUser(null));
    };
  }, []);

  if (!loadedUser || !loadedUserSignatures || !loadedFeatureFlags)
    return <LoadingEl className="AuthUserGate">Loading User Data...</LoadingEl>;

  return <>{children}</>;
};

export default AuthUserGate;
